import {
    flowActionTypes,
    flowActionIcons,
    flowActionIds,
    flowAlertTypes,
    flowRuleTypes,
    faqTypes,
    lgTypes,
    lcTypes,
    chatTypes,
    ruleMatchTypes,
    contactInfoTypes,
    availabilityTypes,
    ticketStages,
    requestStages,
    linkResponseTypes,
    searchResponseTypes,
    searchStages,
    MAX_ACTIONS,
    MAX_RULES,
    paramTypes,
    bookingStages,
    responseSpecialIds,
    ratingTypes,
    patronRecordStages,
    patronRecordResponses,
} from './flowConstants.js';
import { ChatQuestion } from '../libchat/models/ChatQuestion.js';
import springyRegex from '../tools/springyRegex.js';
import StatusRule from '../libchat/StatusRule.js';
import { localize } from '../lang/localize.js';
import { isCoverageInRange } from '../tools/coverageChecker.js';
import { BookingAvailability, BookingLocationInfo, BookingResponse, AppointmentParams } from './bookingModels.js';

/**
 * Response options for patron to bot action
 */
class ActionResponse {
    constructor({
        id = 0,
        actionId = 0,
        text = '',
        freeResponse = false,
        otherResponse = false, // @prop {Boolean} Is this a question response "Other" option
        page = 0, // @prop {Number} if not 0 its for a pagination response, (which page to go to)
        back = false, // @prop {Boolean} if true response triggers "go back 1 stage" on the action
    }) {
        this.id = id;
        this.actionId = actionId;
        this.text = text;
        this.freeResponse = freeResponse;
        this.otherResponse = otherResponse;
        this.page = page;
        this.back = back;
    }
}

/**
 * Class for pending new actions (via rules, etc when editing an action)
 */
class TempAction {
    constructor({
        name,
        type,
        defaultNextAction = flowActionIds.ACTION_END,
    }) {
        this.type = Number(type);
        this.name = name;
        this.defaultNextAction = defaultNextAction;
    }
}

/**
 * Class for LibCal event filter data
 */
class LibCalFilterData {
    constructor({
        categoryIds = [], // @prop {Array<Number>}
        campusIds = [], // @prop {Array<Number>}
        audienceIds = [], // @prop {Array<Number>}
    }) {
        this.categoryIds = categoryIds;
        this.campusIds = campusIds;
        this.audienceIds = audienceIds;
    }

    /**
     * For json serialization, avoid default props.
     * @returns {Object}
     */
    toJSON() {
        const obj = {};
        if (this.categoryIds.length > 0) {
            obj.categoryIds = this.categoryIds;
        }
        if (this.campusIds.length > 0) {
            obj.campusIds = this.campusIds;
        }
        if (this.audienceIds.length > 0) {
            obj.audienceIds = this.audienceIds;
        }
        return obj;
    }
}

/**
 * Rules for handling action responses from the patron
 * Can decide next action to run.
 */
class FlowRule {
    constructor({
        id = 0,
        ruleType = flowRuleTypes.PRESET,
        preset_responses = [], // @prop {Array<Number>} response ids from the QuestionBank Question, or type of contact info to match
        matchType = 0,
        matchVal = [], // @prop {Array<String>}
        target = 0,
        message = '', // @prop {String} show message instead of going to target
        flow_uuid = '', // @prop {String} go to another flow instead of a target action
    }) {
        this.id = Number(id);
        this.ruleType = Number(ruleType);
        if (Array.isArray(preset_responses)) {
            this.preset_responses = preset_responses.map((el) => Number(el));
        }
        this.matchType = Number(matchType);
        this.matchVal = Array.isArray(matchVal) ? matchVal : [];
        this.target = Number(target);
        this.message = message;
        this.flow_uuid = flow_uuid;
    }

    /**
     * When adding a new action to a rule.
     * Save the data here so we only generate it after validation, etc.
     * @param {TempAction} action Temporary action.
     */
    setTempTarget(action) {
        if (action instanceof TempAction) {
            this.newTarget = action;
        }
    }

    /**
     * @returns {TempAction|null}
     */
    getTempTarget() {
        return this.newTarget || null;
    }

    clearTempTarget() {
        this.newTarget = null;
    }

    isPreset() {
        return this.ruleType === flowRuleTypes.PRESET;
    }

    isFreeText() {
        return this.ruleType === flowRuleTypes.FREE;
    }

    isPassAlong() {
        return this.ruleType === flowRuleTypes.PASSALONG;
    }

    /**
     * Copied from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
     * @param {String} string String to escape for use in a regex
     * @returns {String}
     */
    escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
    }

    /**
     * See if text matches a rule.
     * @param {String} text Response text to match
     * @returns {Boolean}
     */
    doesTextMatch(text) {
        // pass along always matches a text response
        if (this.isPassAlong()) {
            return true;
        }
        // skip for no values to test against.
        if (!this.isFreeText() || this.matchVal.length === 0) {
            return false;
        }
        try {
            const escapedVals = this.matchVal.map((val) => {
                return this.escapeRegExp(val);
            });

            switch (this.matchType) {
                case ruleMatchTypes.MATCH_ANY:
                    for (const escVal of escapedVals) {
                        const re = new RegExp(escVal, 'im');
                        // Only one needs to match
                        if (re.test(text)) {
                            return true;
                        }
                    }
                    // None matched
                    return false;
                case ruleMatchTypes.MATCH_ALL:
                    for (const escVal of escapedVals) {
                        const re = new RegExp(escVal, 'im');
                        // if any don't match it fails
                        if (!re.test(text)) {
                            return false;
                        }
                    }
                    // All of them matched
                    return true;
            }
        } catch {
            // just in case something in the regexp blows up.
            return false;
        }
        return false;
    }

    validate() {
        if (this.isPreset() && this.preset_responses.length === 0) {
            throw new Error(
                localize(
                    'errors.validation.ruleResponses',
                    'Response is required.',
                ),
            );
        }
        if (this.isFreeText()) {
            if (!this.matchType) {
                throw new Error(
                    localize(
                        'errors.validation.matchType',
                        'Selecting Any Words or All Words is required.',
                    ),
                );
            }
            if (this.matchVal.length === 0) {
                throw new Error(
                    localize(
                        'errors.validation.ruleMatches',
                        'Keyword/phrase is required.',
                    ),
                );
            }
        }

        if (this.target === 0 && !this.getTempTarget() && !this.message) {
            throw new Error(
                localize(
                    'errors.validation.ruleTarget',
                    'Result action or message is required.',
                ),
            );
        }
    }

    /**
     * For json serialization, avoid default props.
     * @returns {Object}
     */
    toJSON() {
        const obj = {
            id: this.id,
            ruleType: this.ruleType,
            target: this.target,
        };
        if (this.preset_responses.length > 0) {
            obj.preset_responses = this.preset_responses;
        }
        if (this.matchVal.length > 0) {
            obj.matchVal = this.matchVal;
        }
        if (this.matchType !== 0) {
            obj.matchType = this.matchType;
        }
        if (this.message) {
            obj.message = this.message;
        }
        if (this.flow_uuid) {
            obj.flow_uuid = this.flow_uuid;
        }
        return obj;
    }
}

/**
 * Class for the result of processing responses to see what action is next.
 */
class ResponseResult {
    constructor({
        nextActionId = 0,
        text = '',
        passAlong = false,
        message = '',
        flow_uuid = '',
        chainNextSearch = false,
    }) {
        this.nextActionId = Number(nextActionId);
        this.text = text;
        this.passAlong = !!passAlong;
        this.message = message;
        this.flow_uuid = flow_uuid;
        this.chainNextSearch = !!chainNextSearch;
    }
}

class FlowAction {
    constructor({
        id = 0,
        type = 0,
        name = '',
        defaultNextAction = flowActionIds.ACTION_END, // @prop {Number} Next action's id.
        messages = [], // @prop {Array<String>} messages (All types except question)
        subType = 0, // @prop {Number} Depending on type/subtype this will vary what it does.
        rules = [], // @prop {Array<FlowRule>} rules,
    }) {
        this.id = Number(id);
        this.type = Number(type);
        this.name = name;
        this.subType = Number(subType);
        this.messages = messages;

        this.defaultNextAction = defaultNextAction;
        this.rules = rules.map((el) => {
            if (el instanceof FlowRule) {
                return el;
            }
            return new FlowRule(el);
        });
        this.responses = [];
    }

    get icon() {
        return flowActionIcons[this.type] ?? '';
    }

    get alertType() {
        return flowAlertTypes[this.type] ?? 'info';
    }

    getRule(id) {
        return this.rules.find((el) => {
            return el.id === id;
        });
    }

    addRule(rule) {
        if (rule.id === 0) {
            rule.id = this.nextRuleId;
        }
        this.rules.push(rule);
    }

    removeRule(ruleId) {
        const index = this.rules.findIndex((el) => el.id === ruleId);
        if (index <= -1) {
            return;
        }
        this.rules.splice(index, 1);
    }

    /**
     * Has the action reached max rules yet.
     * @returns {Boolean}
     */
    allowNewRules() {
        return this.rules.length < MAX_RULES;
    }

    /**
     * Next rule id to use for this action.
     * Just get highest and add 1;
     * @returns {Number}
     */
    get nextRuleId() {
        let ruleId = 0;
        this.rules.forEach((rule) => {
            if (rule.id > ruleId) {
                ruleId = rule.id;
            }
        });
        return ruleId + 1;
    }

    /**
     * Get all potential next action targets for this action
     * @returns {Array<Number>}
     */
    getAllTargetIds() {
        const targets = [];
        targets.push(this.defaultNextAction);
        this.rules.forEach((rule) => {
            if (rule.target > 0) {
                targets.push(rule.target);
            }
        });
        return targets;
    }

    /**
     * Go back one stage.
     * @returns {Boolean}
     */
    goToPreviousStage() {
        return false;
    }

    /**
     * Text of messages this action should display.
     * @returns {Array<String>}
     */
    getMessages() {
        return this.messages;
    }

    /**
     * Get responses for this action.
     * Fill out in subclasses.
     * @returns {Array<ActionResponse>}
     */
    getResponses() {
        return this.responses;
    }

    /**
     * Get a result for preset responses.
     * Works for only some action types.
     * @param {Number} id Response id
     * @param {String} text Response text.
     * @param {Boolean} chainNextSearch if the search prompt is being reused in the next action
     * @returns {ResponseResult}
     */
    _getResponseForPresetRules(id = 0, text = '', chainNextSearch = false) {
        let rule = null;
        rule = this.rules.find((r) => {
            return (r.isPreset() && r.preset_responses.indexOf(id) >= 0);
        });
        return new ResponseResult({
            nextActionId: rule && rule.target !== 0 ? rule.target : this.defaultNextAction,
            text,
            passAlong: rule ? rule.isPassAlong() : false,
            message: rule ? rule.message : '',
            flow_uuid: rule ? rule.flow_uuid : '',
            chainNextSearch,
        });
    }

    /**
     * Next action result.
     * @returns {ResponseResult}
     */
    getResultForResponse() {
        return new ResponseResult({
            nextActionId: this.defaultNextAction,
        });
    }

    /**
     * Does this action do a search on the response text.
     * @returns {Boolean}
     */
    isSearchAction() {
        return false;
    }

    /**
     * Is this action returning a resrouce from somewhere.
     * @returns {Boolean}
     */
    isResourceAction() {
        return false;
    }

    /**
     * Return ChatQuestion for actions that support it.
     * @returns {null}
     */
    getQuestion() {
        return null;
    }

    /**
     * Validate free text responses.
     * Fill out in subclasses.
     * @param {String} text Free text response
     * @returns {Boolean}
     */
    validateResponse(text = '') {
        return !!text;
    }

    isNextSearchChained() {
        return false;
    }

    validate() {
        if (this.id <= 0) {
            throw new Error('Id is invalid.');
        }
        if (!this.name) {
            throw new Error(localize('errors.validation.nameReq', 'Name is required.'));
        }
        if (!this.type) {
            throw new Error(localize('errors.validation.actionReq', 'Action type is invalid.'));
        }
        if (this.defaultNextAction === 0) {
            throw new Error(localize('errors.validation.nextReq', 'Next Action is required.'));
        }
        this.messages.forEach((msg) => {
            if (msg.length > 500) {
                throw new Error(localize(
                    'errors.validation.msgLength', 'Messages and prompts are limited to 500 characters.',
                ));
            }
        });
    }

    /**
     * For json serialization, avoid default props.
     * Includes props used by subclasses
     * @returns {Object}
     */
    toJSON() {
        const obj = {
            id: this.id,
            type: this.type,
            name: this.name,
            icon: this.icon,
            alertType: this.alertType,
            subType: this.subType,
            defaultNextAction: this.defaultNextAction,
        };
        if (this.isResourceAction()) {
            obj.isResourceAction = true;
        }
        if (this.isSearchAction()) {
            obj.isSearchAction = true;
        }
        if (this.subTypeId !== '') {
            obj.subTypeId = this.subTypeId;
        }
        if (this.messages.length > 0) {
            obj.messages = this.messages;
        }
        if (this.rules.length > 0) {
            obj.rules = this.rules;
        }
        if (this.success_message) {
            obj.success_message = this.success_message;
        }
        if (this.fail_message) {
            obj.fail_message = this.fail_message;
        }
        if (this.question) {
            obj.question = this.question;
        }
        if (this.prompt) {
            obj.prompt = this.prompt;
        }
        if (this.prompt2) {
            obj.prompt2 = this.prompt2;
        }
        if (this.prompt3) {
            obj.prompt3 = this.prompt3;
        }
        if (this.startText) {
            obj.startText = this.startText;
        }
        if (this.dismissText) {
            obj.dismissText = this.dismissText;
        }
        if (this.siteId) {
            obj.siteId = this.siteId;
        }
        if (this.saveAnswer) {
            obj.saveAnswer = this.saveAnswer;
        }
        if (this.previewLink) {
            obj.previewLink = this.previewLink;
        }
        if (this.useForm) {
            obj.useForm = this.useForm;
        }
        if (this.searchFilter) {
            obj.searchFilter = this.searchFilter;
        }
        if (this.botAppt) {
            obj.botAppt = true;
        }
        if (this.apptParams) {
            obj.apptParams = this.apptParams;
        }
        if (this.customHoursString) {
            obj.customHoursString = this.customHoursString;
        }
        if (this.chainNextSearch) {
            obj.chainNextSearch = this.chainNextSearch;
        }
        if (this.faqLinkText) {
            obj.faqLinkText = this.faqLinkText;
        }
        // patron records options can be set to empty
        if (this.checkoutsText || this.checkoutsText === '') {
            obj.checkoutsText = this.checkoutsText;
        }
        if (this.holdsText || this.holdsText === '') {
            obj.holdsText = this.holdsText;
        }
        if (this.finesText || this.finesText === '') {
            obj.finesText = this.finesText;
        }
        // customHoursObj is not converted its never sent back for saving.
        return obj;
    }
}

class WelcomeAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        messages = [],
        startText = '',
        dismissText = '',
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_WELCOME,
            name,
            defaultNextAction,
            messages,
        });
        this.startText = startText;
        this.dismissText = dismissText;
        this.skip_bot = false;
        // Set by widget only.
        this.chat_online = false;
    }

    setChatOnline() {
        this.chat_online = true;
    }

    /**
     * Get responses for this action.
     * Fill out in subclasses.
     * @returns {Array<ActionResponse>}
     */
    getResponses() {
        const responses = [
            new ActionResponse({
                id: 1,
                actionId: this.id,
                text: this.startText,
            }),
            new ActionResponse({
                id: flowActionIds.ACTION_END,
                actionId: this.id,
                text: this.dismissText,
            }),
        ];
        if (this.skip_bot && this.chat_online) {
            responses.push(
                new ActionResponse({
                    id: flowActionIds.ACTION_SKIP,
                    actionId: this.id,
                    text: this.skip_text,
                }),
            );
        }

        return responses;
    }

    /**
     * Next action result.
     * @param {Number} id Chosen response
     * @returns {ResponseResult}
     */
    getResultForResponse({ id = 0 }) {
        let nextActionId = flowActionIds.ACTION_END;
        if (id === 1) {
            nextActionId = this.defaultNextAction;
        }
        if (id === flowActionIds.ACTION_SKIP) {
            nextActionId = flowActionIds.ACTION_SKIP;
        }
        return new ResponseResult({
            nextActionId,
        });
    }

    validate() {
        super.validate();
        if (!this.startText) {
            throw new Error(localize('errors.validation.startReq', 'Start chat text is required.'));
        }
        if (!this.dismissText) {
            throw new Error(localize('errors.validation.dismissReq', 'Dismiss chat text is required.'));
        }
    }
}

class QuestionAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        rules = [], // @prop {Array<FlowRule>} rules,
        subType = 0, // @prop {Number} Question id
        question = null, // @prop {ChatQuestion} Questionbank question for question actions.
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_QUESTION,
            name,
            defaultNextAction,
            subType,
            rules,
        });

        this.setQuestion(question);
        // this is always false for now... pending changes
        this.saveAnswer = false;
    }

    setQuestion(obj) {
        if (obj instanceof ChatQuestion) {
            this.question = obj;
        } else {
            this.question = new ChatQuestion(obj || {});
        }
    }

    /**
     * Return ChatQuestion for actions that support it.
     * @returns {ChatQuestion|null}
     */
    getQuestion() {
        return this.question;
    }

    /**
     * @returns {Array<String>}
     */
    getMessages() {
        return [
            this.question.question,
        ];
    }

    /**
     * Get responses for this action.
     * Fill out in subclasses.
     * @returns {Array<ActionResponse>}
     */
    getResponses() {
        const responses = [];
        if (this.question.isMultiChoice()) {
            this.question.responses.forEach((qResp) => {
                responses.push(
                    new ActionResponse({
                        id: qResp.id,
                        actionId: this.id,
                        text: qResp.text,
                        otherResponse: qResp.id === 0,
                    }),
                );
            });
            return responses;
        }

        responses.push(
            new ActionResponse({
                actionId: this.id,
                freeResponse: true,
            }),
        );
        return responses;
    }

    /**
     * Next action result.
     * @param {Number} id Chosen response
     * @param {String} text String response
     * @returns {ResponseResult}
     */
    getResultForResponse({ id = 0, text = '' }) {
        // Preset match
        let rule = null;
        if (this.question.isMultiChoice()) {
            rule = this.rules.find((r) => {
                return (r.isPreset() && r.preset_responses.indexOf(id) >= 0);
            });
            // Check "other" text response
            if (!rule && this.question.free_response && text !== '') {
                rule = this.rules.find((r) => {
                    return r.doesTextMatch(text);
                });
            }
        } else {
            // Free text match
            rule = this.rules.find((r) => {
                return r.doesTextMatch(text);
            });
        }

        return new ResponseResult({
            nextActionId: rule && rule.target !== 0 ? rule.target : this.defaultNextAction,
            text,
            passAlong: rule ? rule.isPassAlong() : false,
            message: rule ? rule.message : '',
            flow_uuid: rule ? rule.flow_uuid : '',
        });
    }

    validate() {
        super.validate();
        if (this.subType <= 0) {
            throw new Error(localize('errors.validation.questionReq', 'Question is required.'));
        }
    }
}

class RequestAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        rules = [], // @prop {Array<FlowRule>} rules,
        subType = contactInfoTypes.INFO_NAME, // @prop {Number} Type of request
        prompt = localize(
            'defaults.requestNameMsg',
            'What is your name?',
        ), // @prop {String} name prompt
        prompt2 = '', // @prop {String} email prompt
        prompt3 = '', // @prop {String} id prompt
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_REQUEST_INFO,
            name,
            defaultNextAction,
            subType,
            rules,
        });

        this.prompt = prompt;
        this.prompt2 = prompt2;
        this.prompt3 = prompt3;

        this.stage = this._getStartingStage();
        /**
         * @prop {Number} resolvedNextAction Next action can be found via ID or Email
         * That's not always the _last_ response, so we need to save this...
         */
        this.resolvedNextAction = this.defaultNextAction;
    }

    /**
     * Set/Reset the stage property
     * @returns {Number}
     */
    _getStartingStage() {
        let stage = requestStages.EVALUATE;
        if (this.usesName()) {
            stage = requestStages.NAME;
        } else if (this.usesEmail()) {
            stage = requestStages.EMAIL;
        } else if (this.usesId()) {
            stage = requestStages.ID;
        }
        return stage;
    }

    /**
     * @returns {Array<String>}
     */
    getMessages() {
        switch (this.stage) {
            case requestStages.NAME:
                return [
                    this.prompt,
                ];
            case requestStages.EMAIL:
                return [
                    this.prompt2,
                ];
            case requestStages.ID:
                return [
                    this.prompt3,
                ];
        }
        return [];
    }

    /**
     * Get responses for this action.
     * Fill out in subclasses.
     * @returns {Array<ActionResponse>}
     */
    getResponses() {
        return [
            new ActionResponse({
                actionId: this.id,
                freeResponse: true,
            }),
        ];
    }

    _getNextStage() {
        switch (this.stage) {
            case requestStages.NAME:
                if (this.usesId()) {
                    return requestStages.ID;
                }
                if (this.usesEmail()) {
                    return requestStages.EMAIL;
                }
                return requestStages.EVALUATE;
            case requestStages.ID:
                return this.usesEmail() ? requestStages.EMAIL : requestStages.EVALUATE;
            case requestStages.EMAIL:
                return requestStages.EVALUATE;
            default:
                return requestStages.EVALUATE;
        }
    }

    /**
     * Next action result.
     * @param {String} text String response
     * @returns {ResponseResult}
     */
    getResultForResponse({ text = '' }) {
        let nextActionId = this.defaultNextAction;
        let rule = null;
        switch (this.stage) {
            case requestStages.NAME:
                this.stage = this._getNextStage();
                nextActionId = this.id;
                break;
            case requestStages.ID:
                this.stage = this._getNextStage();

                rule = this.rules.find((r) => {
                    if (r.preset_responses.includes(contactInfoTypes.INFO_ID)) {
                        return (r.isFreeText() && r.doesTextMatch(text));
                    }
                    return false;
                });
                if (rule) {
                    this.resolvedNextAction = rule.target !== 0 ? rule.target : this.defaultNextAction;
                }
                nextActionId = this.id;
                break;
            case requestStages.EMAIL:
                this.stage = this._getNextStage();

                rule = this.rules.find((r) => {
                    if (r.preset_responses.includes(contactInfoTypes.INFO_EMAIL)) {
                        return (r.isFreeText() && r.doesTextMatch(text));
                    }
                    return false;
                });
                if (rule) {
                    this.resolvedNextAction = rule.target !== 0 ? rule.target : this.defaultNextAction;
                }
                nextActionId = this.id;
                break;
        }
        // if all the prompts have been asked...
        if (this.stage === requestStages.EVALUATE) {
            nextActionId = this.resolvedNextAction;
            // Reset the stage/action in case action is used again
            this.stage = this._getStartingStage();
            this.resolvedNextAction = this.defaultNextAction;
        }
        return new ResponseResult({
            nextActionId,
            text,
            message: rule ? rule.message : '',
            flow_uuid: rule ? rule.flow_uuid : '',
        });
    }

    validateResponse(text = '') {
        if (!text) {
            return false;
        }
        switch (this.stage) {
            case requestStages.EMAIL:
                return text.match(springyRegex.email) !== null;
            default:
                return true;
        }
    }

    usesName() {
        return this.subType === contactInfoTypes.INFO_NAME
            || this.subType === contactInfoTypes.INFO_NAME_EMAIL
            || this.subType === contactInfoTypes.INFO_NAME_EMAIL_ID
            || this.subType === contactInfoTypes.INFO_NAME_ID;
    }

    usesEmail() {
        return this.subType === contactInfoTypes.INFO_EMAIL
            || this.subType === contactInfoTypes.INFO_NAME_EMAIL_ID
            || this.subType === contactInfoTypes.INFO_EMAIL_ID
            || this.subType === contactInfoTypes.INFO_NAME_EMAIL;
    }

    usesId() {
        return this.subType === contactInfoTypes.INFO_ID
            || this.subType === contactInfoTypes.INFO_NAME_EMAIL_ID
            || this.subType === contactInfoTypes.INFO_EMAIL_ID
            || this.subType === contactInfoTypes.INFO_NAME_ID;
    }

    validate() {
        super.validate();
        if (this.subType <= 0) {
            throw new Error(localize('errors.validation.infoTypeReq', 'Contact Information Type is required.'));
        }
        if ((this.usesName() && this.prompt === '')
            || (this.usesEmail() && this.prompt2 === '')
            || (this.usesId() && this.prompt3 === '')) {
            throw new Error(localize('errors.validation.prompt', 'Prompt cannot be blank.'));
        }
        if (this.prompt.length > 500 || this.prompt2.length > 500 || this.prompt3.length > 500) {
            throw new Error(localize(
                'errors.validation.msgLength', 'Messages and prompts are limited to 500 characters.',
            ));
        }
    }

    /**
     * For json serialization, avoid default props.
     * Includes props used by subclasses
     * @returns {Object}
     */
    toJSON() {
        const obj = super.toJSON();
        obj.usesName = this.usesName();
        obj.usesEmail = this.usesEmail();
        obj.usesId = this.usesId();
        return obj;
    }
}

class LinkAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        prompt = '', // @prop {String} URL.
        prompt2 = '', // @prop {String} link text
        messages = [
            localize('defaults.linkMsg', 'This link may help you:'),
        ], // @prop {Array<String>} messages
        rules = [], // @prop {Array<FlowRule>} rules,
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_LINK,
            name,
            defaultNextAction,
            messages,
            rules,
        });
        this.prompt = prompt;
        this.prompt2 = prompt2;
        this.link_prompt = ''; // set from flow
    }

    getMessages() {
        const link = `<a href="${this.prompt}" class="action_click" target="_blank">${this.prompt2 ? this.prompt2 : this.prompt}</a>`;
        return this.messages.concat(
            [link],
            this.rules.length === 0 ? [] : [this.link_prompt],
        );
    }

    /**
     * Skip responses if there are no rules.
     * @returns {Array<ActionResponse>}
     */
    getResponses() {
        if (this.isResourceAction() && this.rules.length === 0) {
            return [];
        }
        return this.responses;
    }

    isResourceAction() {
        return true;
    }

    /**
     * Next action result.
     * @param {Number} id Chosen response
     * @param {String} text String response
     * @returns {ResponseResult}
     */
    getResultForResponse({ id = 0, text = '' }) {
        return super._getResponseForPresetRules(id, text);
    }

    validate() {
        super.validate();
        if (this.prompt === '') {
            throw new Error(localize('errors.validation.urlReq', 'Website URL is required.'));
        }
        try {
            // this will throw if the url is invalid/not parsable.
            const url = new URL(this.prompt);
            // Also just make sure its a http/s one.
            if (!url.protocol.match('http')) {
                throw new Error();
            }
        } catch {
            throw new Error(localize('errors.validation.urlInvalid', 'Website URL appears invalid.'));
        }
    }
}

class FAQAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        subType = 0, // @prop {Number} Single FAQ or Search type.
        subTypeId = '', // @prop {String} FAQid to show or GroupIds to search
        prompt = '', // @prop {String} Query for FAQ_RESULTS type
        success_message = '', // @prop {String} when search succeeds
        fail_message = '', // @prop {String} when action fails (no faqs found, etc.)
        messages = [], // @prop {Array<String>} messages
        rules = [], // @prop {Array<FlowRule>} rules,
        chainNextSearch = false, // @prop {Bool} Search action chained.
        faqLinkText = '', // @prop {String} the View full FAQ customizable text
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_FAQ,
            name,
            defaultNextAction,
            subType,
            messages,
            rules,
        });
        this.subTypeId = subTypeId;
        this.prompt = prompt;
        this.success_message = success_message;
        this.fail_message = fail_message;
        this.stage = searchStages.PROMPT;
        this.search_prompt = ''; // set from flow
        this.link_prompt = ''; // set from flow
        this.chainNextSearch = !!chainNextSearch;
        this.faqLinkText = faqLinkText;
        this.parentSearchActionId = 0; // For chained searches this holds the start of the chain
    }

    /**
     * @param {String} text Externally generated text (like search results)
     * @returns {Array<String>}
     */
    getMessages(text = '') {
        if (this.isResourceAction()) {
            const messages = this.messages.concat([text]);
            if (this.rules.length > 0) {
                messages.push(this.link_prompt);
            }
            return messages;
        }

        if (this.isSearchAction() && this.stage === searchStages.RESULT) {
            return [
                this.success_message,
                text,
                this.search_prompt,
            ];
        }
        if (this.isSearchAction() && this.stage === searchStages.FAIL) {
            return [
                text || this.fail_message,
                this.search_prompt,
            ];
        }
        return this.messages;
    }

    getResponses() {
        if (this.isSearchAction() && this.stage === searchStages.PROMPT) {
            return [
                new ActionResponse({
                    actionId: this.id,
                    freeResponse: true,
                }),
            ];
        }
        if (this.isResourceAction() && this.rules.length === 0) {
            return [];
        }
        return this.responses;
    }

    isSearchAction() {
        return this.subType === faqTypes.FAQ_SEARCH;
    }

    isResourceAction() {
        return this.subType === faqTypes.FAQ_SINGLE || this.subType === faqTypes.FAQ_RESULTS;
    }

    /**
     * Next action result.
     * @param {Number} id Chosen response
     * @param {String} text String response
     * @returns {ResponseResult}
     */
    getResultForResponse({ id = 0, text = '' }) {
        if (this.isSearchAction()) {
            if (text !== '' && this.stage !== searchStages.CHAINED) {
                this.stage = searchStages.RESULT;
                const response = new ResponseResult({
                    nextActionId: this.id,
                    text,
                    chainNextSearch: this.chainNextSearch,
                });
                if (this.parentSearchActionId > 0) {
                    response.nextActionId = this.parentSearchActionId;
                    response.chainNextSearch = true;
                }

                return response;
            }
            // Reset the stage in case action is used again
            this.stage = searchStages.PROMPT;
        }
        return super._getResponseForPresetRules(id, text, this.chainNextSearch);
    }

    isNextSearchChained() {
        return this.chainNextSearch;
    }

    validate() {
        super.validate();
        if (this.subType <= 0) {
            throw new Error(localize('errors.validation.faqTypeReq', 'FAQ Type is required.'));
        }
        if (this.subType === faqTypes.FAQ_SINGLE && !this.subTypeId) {
            throw new Error(localize('errors.validation.faqIdReq', 'FAQ ID is required.'));
        }
        if (this.subType === faqTypes.FAQ_SEARCH && this.subTypeId === '') {
            throw new Error(localize('errors.validation.groupsReq', 'Groups are required.'));
        }
        if (this.subType === faqTypes.FAQ_RESULTS && (this.subTypeId === '' || this.prompt === '')) {
            throw new Error(localize('errors.validation.faqResults', 'Group and Search are required.'));
        }
    }
}

class ChatAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        subType = 0, // @prop {Number} Where to send chat (op/dept/coop)
        subTypeId = '', // @prop {String} Id/uuid for op/dept/coop
        useFallback = true,
        fail_message = localize('defaults.chatFailMsg', 'We\'re sorry, operators are not online currently.'), // @prop {String} when action fails (chat is offline)
        messages = [
            localize('defaults.chatMsg', 'We\'ll check to see if an operator is online, please wait one moment.'),
        ], // @prop {Array<String>} messages
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_CHAT,
            name,
            defaultNextAction,
            subType,
            messages,
        });
        this.subTypeId = subTypeId;
        this.useFallback = !!useFallback;
        this.fail_message = fail_message;
    }

    /**
     * Status rule for this action
     * So we can check on/offline status.
     * @returns {StatusRule}
     */
    getStatusRule() {
        const r = new StatusRule({
            u: this.subType === chatTypes.SEND_OP
                ? this.subTypeId
                : 0,
            d: this.subType === chatTypes.SEND_DEPT
                ? [this.subTypeId]
                : [],
        });
        return r;
    }

    validate() {
        super.validate();
        if (this.subType <= 0) {
            throw new Error(localize('errors.validation.chatTypeReq', 'Chat Action Type is required.'));
        }
        if (this.subType === chatTypes.SEND_OP && !Number(this.subTypeId)) {
            throw new Error(localize('errors.validation.opReq', 'Operator is required.'));
        }
        if (this.subType === chatTypes.SEND_DEPT && !Number(this.subTypeId)) {
            throw new Error(localize('errors.validation.deptReq', 'Department is required.'));
        }
    }
}

class TicketAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        subType = 0, // @prop {Number} Queue id to use.
        // the next three are no longer customizable
        startText = localize('defaults.startTicketMsg', 'Submit Ticket.'),
        dismissText = localize('defaults.dismissTicketMsg', 'Don\'t submit.'),
        messages = [

            localize('defaults.ticketMsg', 'Would you like to submit a ticket?'),
        ], // @prop {Array<String>} messages
        prompt = localize('defaults.ticketPrompt', 'Please type your query in below.'),
        prompt2 = localize(
            'defaults.contactPrompt',
            'Please provide your email address in order to submit this ticket.',
        ),
        useForm = false, // @prop {Boolean} Use question form questions
        success_message = localize(
            'defaults.ticketSuccessMsg',
            'Your ticket has been submitted. Someone will be in touch soon. Thank you!',
        ), // @prop {String} when action succeeds
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_TICKET,
            name,
            defaultNextAction,
            subType,
            messages,
        });
        this.startText = startText;
        this.dismissText = dismissText;
        this.prompt = prompt;
        this.prompt2 = prompt2;
        this.useForm = !!useForm;
        this.success_message = success_message;
        /** @prop {Number} stage The part of the action (prompt, contact, question) */
        this.stage = ticketStages.PROMPT;
    }

    /**
     * @returns {Array<String>}
     */
    getMessages() {
        switch (this.stage) {
            case ticketStages.PROMPT:
                // initial prompt
                return this.messages;
            case ticketStages.CONTACT:
                return [
                    this.prompt2,
                ];
            case ticketStages.QUESTION:
                return [
                    this.prompt,
                ];
        }
        return [];
    }

    /**
     * Get responses for this action.
     * @returns {Array<ActionResponse>}
     */
    getResponses() {
        if (this.stage === ticketStages.PROMPT) {
            // start or not buttons
            return [
                new ActionResponse({
                    id: 1,
                    actionId: this.id,
                    text: this.startText,
                }),
                new ActionResponse({
                    id: 0,
                    actionId: this.id,
                    text: this.dismissText,
                }),
            ];
        }
        if (this.stage === ticketStages.FORM) {
            return [];
        }
        // otherwise its email or question text
        return [
            new ActionResponse({
                id: 1,
                actionId: this.id,
                freeResponse: true,
            }),
        ];
    }

    /**
     * Next action result.
     * Update the stage as we go.
     * @param {Number} id Response id or Ticket id (if one was made)
     * @returns {ResponseResult}
     */
    getResultForResponse({ id = 0 }) {
        let nextActionId = this.defaultNextAction;
        switch (this.stage) {
            case ticketStages.PROMPT:
                // want to submit ticket
                if (id !== 0) {
                    if (this.useForm) {
                        this.stage = ticketStages.FORM;
                    } else {
                        this.stage = ticketStages.CONTACT;
                    }
                    nextActionId = this.id;
                }
                break;
            case ticketStages.CONTACT:
                this.stage = ticketStages.QUESTION;
                nextActionId = this.id;
                break;
            case ticketStages.QUESTION:
            case ticketStages.FORM:
                // successful ticket === end chat.
                if (id > 0) {
                    nextActionId = flowActionIds.ACTION_END;
                }
                break;
        }
        return new ResponseResult({
            nextActionId,
        });
    }

    validateResponse(text = '') {
        if (!text) {
            return false;
        }
        if (this.stage !== ticketStages.CONTACT) {
            return true;
        }
        // Contact must be email here.
        if (text.match(springyRegex.email) !== null) {
            return true;
        }
        return false;
    }

    validate() {
        super.validate();
        if (this.subType <= 0) {
            throw new Error(localize('errors.validation.queueReq', 'Queue is required.'));
        }
        if (this.startText === '' || this.dismissText === '') {
            throw new Error(localize('errors.validation.buttonLabelReq', 'Button labels are required.'));
        }
        if (this.prompt === '') {
            throw new Error(localize('errors.validation.promptReq', 'Prompt cannot be blank.'));
        }
        if (this.prompt.length > 500) {
            throw new Error(localize(
                'errors.validation.msgLength', 'Messages and prompts are limited to 500 characters.',
            ));
        }
    }
}

class LibGuidesAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        subType = 0, // @prop {Number} Type of LG action
        subTypeId = '', // @prop {String} Id for guide/az
        siteId = 0, // @prop {Number} LG Site id.
        success_message = '', // @prop {String} when search succeeds
        fail_message = '', // @prop {String} when action fails (no faqs found, etc.)
        prompt = '', // @prop {String} URL for "other"
        prompt2 = '', // @prop {String} link text
        messages = [
            localize('defaults.guideMsg', 'What can I help you find today in our LibGuides System?'),
        ], // @prop {Array<String>} messages
        rules = [], // @prop {Array<FlowRule>} rules,
        chainNextSearch = false, // @prop {Boolean} Search action chained.
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_LIBGUIDES,
            name,
            defaultNextAction,
            subType,
            messages,
            rules,
        });
        this.subTypeId = subTypeId;
        this.siteId = Number(siteId || 0);
        this.success_message = success_message;
        this.fail_message = fail_message;
        this.prompt = prompt;
        this.prompt2 = prompt2;
        this.stage = searchStages.PROMPT;
        this.search_prompt = ''; // set from flow
        this.link_prompt = ''; // set from flow
        this.chainNextSearch = !!chainNextSearch;
        this.parentSearchActionId = 0; // For chained searches this holds the start of the chain
    }

    /**
     * @param {String} text Externally generated text (like search results)
     * @returns {Array<String>}
     */
    getMessages(text = '') {
        if (this.isResourceAction()) {
            if (this.subType === lgTypes.LG_OTHER) {
                text = `<a href="${this.prompt}" class="action_click" target="_blank">${this.prompt2 ? this.prompt2 : this.prompt}</a>`;
            }
            const messages = this.messages.concat([text]);
            if (this.rules.length > 0) {
                messages.push(this.link_prompt);
            }
            return messages;
        }

        if (this.isSearchAction() && this.stage === searchStages.RESULT) {
            return [
                this.success_message,
                text,
                this.search_prompt,
            ];
        }
        if (this.isSearchAction() && this.stage === searchStages.FAIL) {
            return [
                text || this.fail_message,
                this.search_prompt,
            ];
        }
        return this.messages;
    }

    getResponses() {
        if (this.isSearchAction() && this.stage === searchStages.PROMPT) {
            return [
                new ActionResponse({
                    actionId: this.id,
                    freeResponse: true,
                }),
            ];
        }
        if (this.isResourceAction() && this.rules.length === 0) {
            return [];
        }
        return this.responses;
    }

    isSearchAction() {
        return this.subType === lgTypes.GUIDE_SEARCH
            || this.subType === lgTypes.AZ_SEARCH;
    }

    isResourceAction() {
        return this.subType === lgTypes.GUIDE_SINGLE
            || this.subType === lgTypes.AZ_SINGLE
            || this.subType === lgTypes.LG_OTHER;
    }

    isNextSearchChained() {
        return this.chainNextSearch;
    }

    /**
     * Next action result.
     * @param {Number} id Chosen response
     * @param {String} text String response
     * @returns {ResponseResult}
     */
    getResultForResponse({ id = 0, text = '' }) {
        if (this.isSearchAction()) {
            if (text !== '' && this.stage !== searchStages.CHAINED) {
                this.stage = searchStages.RESULT;
                const response = new ResponseResult({
                    nextActionId: this.id,
                    text,
                    chainNextSearch: this.chainNextSearch,
                });
                if (this.parentSearchActionId > 0) {
                    response.nextActionId = this.parentSearchActionId;
                    response.chainNextSearch = true;
                }

                return response;
            }
            // Reset the stage in case action is used again
            this.stage = searchStages.PROMPT;
        }
        return super._getResponseForPresetRules(id, text, this.chainNextSearch);
    }

    validate() {
        super.validate();
        if (this.siteId <= 0) {
            throw new Error(localize('errors.validation.siteReq', 'Selecting a Site is Required.'));
        }
        if (this.subType <= 0) {
            throw new Error(localize('errors.validation.typeReq', 'Selecting an Item is Required.'));
        }
        if ((this.subType === lgTypes.GUIDE_SINGLE || this.subType === lgTypes.AZ_SINGLE) && !this.subTypeId) {
            throw new Error(localize('errors.validation.itemReq', 'Item Id is required.'));
        }
        if (this.subType === lgTypes.LG_OTHER) {
            if (this.prompt === '') {
                throw new Error(localize('errors.validation.urlReq', 'Website URL is required.'));
            }
            try {
                // this will throw if the url is invalid/not parsable.
                const url = new URL(this.prompt);
                // Also just make sure its a http/s one.
                if (!url.protocol.match('http')) {
                    throw new Error();
                }
            } catch {
                throw new Error(localize('errors.validation.urlInvalid', 'Website URL appears invalid.'));
            }
        }
    }
}

class LibCalAction extends FlowAction {
    /** @prop BookingLocationInfo|null */
    #locationInfo = null;
    /** @prop BookResponse|null */
    #bookingResp = null;
    /** @prop BookingAvailability|null */
    #bookingAvailability = null;

    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        subType = 0, // @prop {Number} what LC action to take
        siteId = 0, // @prop {Number} LC Site id.
        subTypeId = '', // @prop {String} Id for location or calendar
        success_message = '', // @prop {String} when search succeeds
        fail_message = '', // @prop {String} when action fails (no faqs found, etc.)
        messages = [
            localize('defaults.calMsg', 'You may find the information you\'re looking for in our LibCal system!'),
        ], // @prop {Array<String>} messages
        rules = [], // @prop {Array<FlowRule>} rules,
        searchFilter = null, // @prop {LibCalFilterData|null} This is the filter options object that will be used for Event search
        botAppt = false, // @prop {Boolean} Can the bot book appointments (subtype 3 only)
        apptParams = null, // @prop {AppointmentParams|null} For bot booking appointments
        chainNextSearch = false, // @prop {Boolean} Search action chained.
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_LIBCAL,
            name,
            defaultNextAction,
            subType,
            messages,
            rules,
        });
        this.subTypeId = subTypeId;
        this.siteId = Number(siteId || 0);
        this.success_message = success_message;
        this.fail_message = fail_message;
        this.stage = searchStages.PROMPT;
        this.search_prompt = ''; // set from flow
        this.link_prompt = ''; // set from flow
        this.searchFilter = searchFilter ? new LibCalFilterData(searchFilter) : null;
        this.botAppt = !!botAppt;
        this.apptParams = apptParams ? new AppointmentParams(apptParams) : null;
        this.chainNextSearch = !!chainNextSearch;
        this.parentSearchActionId = 0; // For chained searches this holds the start of the chain
    }

    /**
     * @returns {BookingResponse|null}
     */
    getBookingInfo() {
        return this.#bookingResp;
    }

    /**
     * Location name and user name for bot booking
     * @returns {String}
     */
    getLocationAndUserName() {
        if (!this.#locationInfo || !this.#bookingResp) {
            return '';
        }
        const locationName = this.#locationInfo.name;
        let userName = '';
        const group = this.#locationInfo.getGroup(this.#bookingResp.groupId);
        if (group) {
            const user = group.getUser(this.#bookingResp.userId);
            if (user) {
                userName = user.name;
            }
        }
        return `${locationName}: ${userName}`.trim();
    }

    /**
     * For sending to LC when libauth is active.
     * @returns {String}
     */
    #getBookingAppointmentLink() {
        const url = `https://${this.#locationInfo.domain}/appointment/${this.#bookingResp.userId}?lid=${this.#bookingResp.locationId}&g=${this.#bookingResp.groupId}&c=${this.#bookingResp.categoryId}`;
        return `<a href="${url}" class="action_click" data-actionid="${this.id}">${url}</a>`;
    }

    /**
     * Need this for when we have to repeat or go back a step
     * And its complicated to figure out
     * @returns {Number}
     */
    getPreviousBookingStage() {
        if (this.stage === bookingStages.GROUP_PROMPT) {
            // first stage
            return bookingStages.GROUP_PROMPT;
        }
        if (this.stage === bookingStages.FORM) {
            return bookingStages.TIME_PROMPT;
        }
        if (this.stage === bookingStages.TIME_PROMPT) {
            return bookingStages.DATE_PROMPT;
        }
        if (this.stage === bookingStages.CAT_PROMPT) {
            // first stage if group is specific in settings
            if (this.apptParams.groupId > 0) {
                return bookingStages.CAT_PROMPT;
            }
            return bookingStages.GROUP_PROMPT;
        }
        if (this.stage === bookingStages.CAT_USER_PROMPT
            || this.stage === bookingStages.USER_PROMPT) {
            // first stage if category is specific in settings
            if (this.apptParams.categoryId > 0) {
                return this.stage;
            }
            const group = this.#locationInfo.getGroup(this.#bookingResp.groupId);
            if (!group) {
                return bookingStages.FAIL;
            }
            // if categories are there...
            if (group.categories.length > 0) {
                return bookingStages.CAT_PROMPT;
            }
            // if group was pre-set
            if (this.apptParams.groupId > 0) {
                // this is first stage then
                return this.stage;
            }
            return bookingStages.GROUP_PROMPT;
        }

        if (this.stage === bookingStages.DATE_PROMPT) {
            const group = this.#locationInfo.getGroup(this.#bookingResp.groupId);
            if (!group) {
                return bookingStages.FAIL;
            }
            if (group.users.length > 1) {
                // user or cat user
                if (group.categories.length === 0) {
                    return bookingStages.USER_PROMPT;
                }
                return bookingStages.CAT_USER_PROMPT;
            }
            // 1 user, category pre-chosen, this is the first stage
            if (this.apptParams.categoryId > 0) {
                return bookingStages.CAT_USER_PROMPT;
            }
            if (group.categories.length === 0) {
                return bookingStages.GROUP_PROMPT;
            }
            return bookingStages.CAT_PROMPT;
        }

        return bookingStages.FAIL;
    }

    /**
     * Go back one stage.
     * @returns {Boolean}
     */
    goToPreviousStage() {
        if (!this.isBotBooking()) {
            return false;
        }
        this.stage = this.getPreviousBookingStage();

        // delete some response data at certain stages
        if (this.stage === bookingStages.DATE_PROMPT) {
            this.#bookingResp.date = '';
        }
        if (this.stage === bookingStages.USER_PROMPT
            || this.stage === bookingStages.CAT_USER_PROMPT) {
            this.#bookingResp.userId = 0;
        }
        if (this.stage === bookingStages.CAT_PROMPT) {
            this.#bookingResp.categoryId = 0;
        }
        if (this.stage === bookingStages.GROUP_PROMPT_PROMPT) {
            this.#bookingResp.groupId = 0;
        }
        return true;
    }

    #getBookingMessages(text = '') {
        if (!this.#locationInfo || !this.apptParams) {
            throw new Error();
        }
        if (this.stage === bookingStages.FAIL) {
            return [text];
        }
        if (!this.#bookingResp) {
            this.#bookingResp = new BookingResponse({
                siteId: this.siteId,
                locationId: Number(this.subTypeId),
            });
        }
        let messages = [];
        // if we had to repeat a stage, a message was sent back
        // something like "no times found"
        if (text !== '') {
            messages.push(text);
        }
        let group = null;
        let cat = null;
        let user = null;
        if (this.stage === bookingStages.GROUP_PROMPT) {
            messages = messages.concat(this.messages); // so it clones.
            // pick a group
            if (this.apptParams.groupId === 0) {
                const prompt = this.apptParams.promptGroup.replace('{nameToken}', this.#locationInfo.name);
                messages.push(prompt);
                return messages;
            }
            // check for group?
            group = this.#locationInfo.getGroup(this.apptParams.groupId);
            if (!group) {
                throw new Error();
            }
            this.#bookingResp.groupId = group.id;
            this.stage = bookingStages.CAT_PROMPT;
        }

        if (!group) {
            group = this.#locationInfo.getGroup(this.#bookingResp.groupId);
        }

        if (this.stage === bookingStages.CAT_PROMPT) {
            // category pick
            if (this.apptParams.categoryId === 0 && group.categories.length > 0) {
                const prompt = this.apptParams.promptCat.replace('{nameToken}', group.name);
                messages.push(prompt);
                return messages;
            }

            // specific category
            if (this.apptParams.categoryId > 0 && group.categories.length > 0) {
                cat = group.getCategory(this.apptParams.categoryId);
                if (cat) {
                    // what prompt shows now?
                    this.#bookingResp.categoryId = cat.id;
                    this.stage = bookingStages.CAT_USER_PROMPT;
                }
            } else {
                // no categories
                this.stage = bookingStages.USER_PROMPT;
            }
        }

        if (!cat) {
            cat = group.getCategory(this.#bookingResp.categoryId);
        }

        if (this.stage === bookingStages.CAT_USER_PROMPT) {
            if (group.users.length > 0) {
                const prompt = this.apptParams.promptCatUser.replace('{nameToken}', cat.name);
                messages.push(prompt);
                return messages;
            } else {
                this.stage = bookingStages.DATE_PROMPT;
            }
        }

        if (this.stage === bookingStages.USER_PROMPT) {
            if (group.users.length > 0) {
                const prompt = this.apptParams.promptUser.replace('{nameToken}', group.name);
                messages.push(prompt);
                return messages;
            } else {
                this.stage = bookingStages.DATE_PROMPT;
            }
        }

        user = group.getUser(this.#bookingResp.userId);

        if (this.stage === bookingStages.DATE_PROMPT) {
            const prompt = this.apptParams.promptDate.replace('{nameToken}', user ? user.name : '');
            messages.push(prompt);
            return messages;
        }

        if (this.stage === bookingStages.TIME_PROMPT) {
            const prompt = `${this.apptParams.promptTime.replace('{nameToken}', user ? user.name : '')} ${this.#bookingAvailability.timezoneNote}`;
            messages.push(prompt);
            return messages;
        }

        if (this.stage === bookingStages.AUTHLINK) {
            const prompt = this.apptParams.promptAuth.replace('{libAuthLink}', this.#getBookingAppointmentLink());
            messages.push(prompt);
            messages.push(this.link_prompt);
            return messages;
        }

        // Final stage...
        return [
            this.apptParams.successMsg,
            this.link_prompt,
        ];
    }

    #getBookingBackResponse() {
        return new ActionResponse({
            id: responseSpecialIds.STAGE_BACK,
            action: this.id,
            text: this.#locationInfo.language?.back || 'Return To Prior Step',
            back: true,
        });
    }

    #getBookingResponses({ page = 0 }) {
        const responses = [];
        if (this.stage === bookingStages.GROUP_PROMPT) {
            // list groups
            this.#locationInfo.groups.forEach((group) => {
                responses.push(
                    new ActionResponse({
                        id: group.id,
                        actionId: this.id,
                        text: group.name,
                    }),
                );
            });
            return responses;
        }

        if (this.stage === bookingStages.CAT_PROMPT) {
            // list categories
            const group = this.#locationInfo.getGroup(this.#bookingResp.groupId);
            group.categories.forEach((cat) => {
                responses.push(
                    new ActionResponse({
                        id: cat.id,
                        actionId: this.id,
                        text: cat.name,
                    }),
                );
            });
            if (this.getPreviousBookingStage() !== this.stage) {
                responses.push(this.#getBookingBackResponse());
            }
            return responses;
        }

        if (this.stage === bookingStages.CAT_USER_PROMPT
            || this.stage === bookingStages.USER_PROMPT) {
            // list users
            const group = this.#locationInfo.getGroup(this.#bookingResp.groupId);
            group.users.forEach((user) => {
                responses.push(
                    new ActionResponse({
                        id: user.id,
                        actionId: this.id,
                        text: user.name,
                    }),
                );
            });
            if (this.getPreviousBookingStage() !== this.stage) {
                responses.push(this.#getBookingBackResponse());
            }
            return responses;
        }

        if (this.stage === bookingStages.DATE_PROMPT) {
            const dateCount = this.#bookingAvailability.dates.length;
            if (dateCount <= 14) {
                this.#bookingAvailability.dates.forEach((date, index) => {
                    responses.push(
                        new ActionResponse({
                            id: index,
                            actionId: this.id,
                            text: date,
                        }),
                    );
                });
            } else {
                if (page === 0) {
                    page = 1;
                }
                const pagination = 15;
                let startIndex = 0;
                let endIndex = 15;
                if (page > 1) {
                    startIndex = (page - 1) * pagination;
                    endIndex = startIndex + pagination;
                    responses.push(
                        new ActionResponse({
                            id: responseSpecialIds.PAGE_BACK,
                            actionId: this.id,
                            text: this.#bookingAvailability.language?.prev || 'See Previous Dates',
                            page: page - 1,
                        }),
                    );
                }

                this.#bookingAvailability.dates.forEach((date, index) => {
                    if (index < startIndex || index >= endIndex) {
                        return;
                    }
                    responses.push(
                        new ActionResponse({
                            id: index,
                            actionId: this.id,
                            text: date,
                        }),
                    );
                });

                if (endIndex <= this.#bookingAvailability.dates.length) {
                    responses.push(
                        new ActionResponse({
                            id: responseSpecialIds.PAGE_FORWARD,
                            actionId: this.id,
                            text: this.#bookingAvailability.language?.next || 'See More Dates',
                            page: page + 1,
                        }),
                    );
                }
            }
            responses.push(this.#getBookingBackResponse());
            return responses;
        }

        if (this.stage === bookingStages.TIME_PROMPT) {
            this.#bookingAvailability.times.forEach((time, index) => {
                responses.push(
                    new ActionResponse({
                        id: index,
                        actionId: this.id,
                        text: time.start,
                    }),
                );
            });
            responses.push(this.#getBookingBackResponse());
            return responses;
        }
        // for last stage return the normal yes/no
        return this.responses;
    }

    #getBookingResultForResponse({ id = 0 }) {
        if (this.stage === bookingStages.GROUP_PROMPT) {
            this.#bookingResp.groupId = id;
            // sometimes cat prompt is skipped (no categories)
            const group = this.#locationInfo.getGroup(id);
            if (!group) {
                this.stage = bookingStages.FAIL;
            } else if (group.categories.length === 0) {
                this.stage = bookingStages.USER_PROMPT;
            } else {
                this.stage = bookingStages.CAT_PROMPT;
            }
            this.stage = bookingStages.CAT_PROMPT;
            return new ResponseResult({
                nextActionId: this.id,
            });
        }

        if (this.stage === bookingStages.CAT_PROMPT) {
            this.#bookingResp.categoryId = id;
            this.stage = bookingStages.CAT_USER_PROMPT;
            return new ResponseResult({
                nextActionId: this.id,
            });
        }

        if (this.stage === bookingStages.CAT_USER_PROMPT
            || this.stage === bookingStages.USER_PROMPT) {
            this.#bookingResp.userId = id;
            this.stage = bookingStages.DATE_PROMPT;
            return new ResponseResult({
                nextActionId: this.id,
            });
        }

        if (this.stage === bookingStages.DATE_PROMPT) {
            // id the index of the date in the date array
            this.#bookingResp.date = this.#bookingAvailability.dates[id] || '';
            if (this.#bookingAvailability.areAllUsersAuthed()) {
                this.stage = bookingStages.AUTHLINK;
            } else {
                this.stage = bookingStages.TIME_PROMPT;
            }
            return new ResponseResult({
                nextActionId: this.id,
            });
        }

        if (this.stage === bookingStages.TIME_PROMPT) {
            this.#bookingResp.setTime(this.#bookingAvailability.times[id]);
            if (this.#bookingAvailability.areAllUsersAuthed()) {
                this.stage = bookingStages.AUTHLINK;
            } else {
                this.stage = bookingStages.FORM;
            }
            return new ResponseResult({
                nextActionId: this.id,
            });
        }

        return super._getResponseForPresetRules(id, '');
    }

    /**
     * @param {String} text Externally generated text (like search results)
     * @returns {Array<String>}
     */
    getMessages(text = '') {
        if (this.isBotBooking()) {
            // stages here...
            return this.#getBookingMessages(text);
        }
        if (this.isResourceAction()) {
            const messages = this.messages.concat([text]);
            if (this.rules.length > 0) {
                messages.push(this.link_prompt);
            }
            return messages;
        }

        if (this.isSearchAction() && this.stage === searchStages.RESULT) {
            return [
                this.success_message,
                text,
                this.search_prompt,
            ];
        }
        if (this.isSearchAction() && this.stage === searchStages.FAIL) {
            return [
                text || this.fail_message,
                this.search_prompt,
            ];
        }
        return this.messages;
    }

    getResponses({ page = 0 }) {
        if (this.isBotBooking()) {
            return this.#getBookingResponses({ page });
        }
        if (this.isSearchAction() && this.stage === searchStages.PROMPT) {
            return [
                new ActionResponse({
                    actionId: this.id,
                    freeResponse: true,
                }),
            ];
        }
        if (this.isResourceAction() && this.rules.length === 0) {
            return [];
        }
        return this.responses;
    }

    isSearchAction() {
        return this.subType === lcTypes.EVENT_SEARCH;
    }

    isResourceAction() {
        return this.subType === lcTypes.BOOKING_APPT
            || this.subType === lcTypes.BOOKING_EQUIP
            || this.subType === lcTypes.BOOKING_SPACE
            || this.subType === lcTypes.HOURS_TODAY
            || this.subType === lcTypes.HOURS_WEEK;
    }

    isBotBooking() {
        return this.subType === lcTypes.BOOKING_APPT && this.botAppt;
    }

    /**
     * Quick way to know if its an hours action
     * @returns {Boolean}
     */
    isHours() {
        return this.subType === lcTypes.HOURS_TODAY
            || this.subType === lcTypes.HOURS_WEEK;
    }

    isLocationInfoSet() {
        return this.#locationInfo !== null;
    }

    setLocationInfo(info) {
        if (info instanceof BookingLocationInfo) {
            this.#locationInfo = info;
        }
    }

    setBookingAvailability(data) {
        if (data instanceof BookingAvailability) {
            this.#bookingAvailability = data;
        }
    }

    /**
     * Next action result.
     * @param {Number} id Chosen response
     * @param {String} text String response
     * @returns {ResponseResult}
     */
    getResultForResponse({ id = 0, text = '' }) {
        if (this.isBotBooking()) {
            return this.#getBookingResultForResponse({ id, text });
        }
        if (this.isSearchAction()) {
            if (text !== '' && this.stage !== searchStages.CHAINED) {
                this.stage = searchStages.RESULT;
                const response = new ResponseResult({
                    nextActionId: this.id,
                    text,
                    chainNextSearch: this.chainNextSearch,
                });

                if (this.parentSearchActionId > 0) {
                    response.nextActionId = this.parentSearchActionId;
                    response.chainNextSearch = true;
                }
                return response;
            }
            // Reset the stage in case action is used again
            this.stage = searchStages.PROMPT;
        }
        return super._getResponseForPresetRules(id, text, this.chainNextSearch);
    }

    isNextSearchChained() {
        return this.chainNextSearch;
    }

    validate() {
        super.validate();
        if (this.siteId <= 0) {
            throw new Error(localize('errors.validation.siteReq', 'Selecting a Site is Required.'));
        }
        if (this.subType <= 0) {
            throw new Error(localize('errors.validation.typeReq', 'Selecting an Item is Required.'));
        }
        if (this.subType !== lcTypes.EVENT_SEARCH && (!this.subTypeId || this.subTypeId === '0')) {
            throw new Error(localize('errors.validation.locationReq', 'Location or Library is required.'));
        }
    }
}

class LibWizardAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        subType = 0, // @prop {Number} what LZ action to take
        siteId = 0, // @prop {Number} LZ Site id.
        subTypeId = '', // @prop {String} Id for form, quiz, etc.
        messages = [
            localize('defaults.lwzMsg', 'Please head to our LibWizard form and fill it out!'),
        ], // @prop {Array<String>} messages
        rules = [], // @prop {Array<FlowRule>} rules
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_LIBWIZARD,
            name,
            defaultNextAction,
            subType,
            messages,
            rules,
        });
        this.subTypeId = subTypeId;
        this.siteId = Number(siteId || 0);
    }

    /**
     * @param {String} text Externally generated text (like search results)
     * @returns {Array<String>}
     */
    getMessages(text = '') {
        const messages = this.messages.concat([text]);
        if (this.rules.length > 0) {
            messages.push(this.link_prompt);
        }
        return messages;
    }

    /**
     * Skip responses if there are no rules.
     * @returns {Array<ActionResponse>}
     */
    getResponses() {
        if (this.isResourceAction() && this.rules.length === 0) {
            return [];
        }
        return this.responses;
    }

    isResourceAction() {
        return true;
    }

    /**
     * Next action result.
     * @param {Number} id Chosen response
     * @param {String} text String response
     * @returns {ResponseResult}
     */
    getResultForResponse({ id = 0, text = '' }) {
        return super._getResponseForPresetRules(id, text);
    }

    validate() {
        super.validate();
        if (this.siteId <= 0) {
            throw new Error(localize('errors.validation.siteReq', 'Selecting a Site is Required.'));
        }
        if (this.subType <= 0) {
            throw new Error(localize('errors.validation.typeReq', 'Selecting an Item is Required.'));
        }
        if (!this.subTypeId || this.subTypeId === '0') {
            throw new Error(localize('errors.validation.itemReq', 'Item Id is required.'));
        }
    }
}

class RatingAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        subType = 0, // @prop {Number} what rating to use
        subTypeId = '', // @prop {String} what survey id to use.
        defaultNextAction = flowActionIds.ACTION_END, // always goes to end
        messages = [
            localize('defaults.ratingMsg', 'We\'d love to get your feedback on this interaction.'),
        ],
        prompt = '', // @prop {String} used in widget only for survey low label NOT SAVED
        prompt2 = '', // @prop {String} used in widget only for survey high label NOT SAVED
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_RATING,
            name,
            defaultNextAction,
            subType,
            messages,
        });
        this.subTypeId = subTypeId;
        this.prompt = prompt;
        this.prompt2 = prompt2;
    }

    /**
     * Get responses for this action.
     * @returns {Array<ActionResponse>}
     */
    getResponses() {
        // this is an empty response..
        // basically keeps the bot from moving onto the next action
        // instead submitting the rating form triggers the next action
        return [
            new ActionResponse({
                id: flowActionIds.ACTION_END,
                actionId: this.id,
                text: '',
            }),
        ];
    }

    validate() {
        super.validate();
        if (this.subType === ratingTypes.NPS_SURVEY && !this.subTypeId) {
            throw new Error(localize('errors.validation.typeReq', 'Selecting an Item is Required.'));
        }
    }
}

class ParamAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        subType = 0, // @prop {Number} what param to look at
        rules = [], // @prop {Array<FlowRule>} rules
        customHoursString = '', // @prop {String} for builder, JSON custom hours
        customHoursObj = null, // @prop {Object} for widget, custom hours for testing against
        siteId = 0, // @prop {Number} LC site id for LC HOURs param
        subTypeId = '', // @prop {String} Id for location or calendar
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_PARAMS,
            name,
            defaultNextAction,
            subType,
            rules,
        });
        this.customHoursString = customHoursString;
        this.customHoursObj = customHoursObj;
        this.siteId = siteId;
        this.subTypeId = subTypeId;
    }

    /**
     * Next action result.
     * @param {Number} id Chosen response
     * @param {String} text String response
     * @returns {ResponseResult}
     */
    getResultForResponse({ id = 0, text = '' }) {
        return super._getResponseForPresetRules(id, text);
    }

    validate() {
        super.validate();
        if (this.subType <= 0) {
            throw new Error(localize('errors.validation.typeReq', 'Selecting an Item is Required.'));
        }
        if (this.subType === paramTypes.PARAM_OPEN_HOURS && !this.customHoursString) {
            throw new Error(localize('errors.validation.openHours', 'Please set your Open Hours to continue.'));
        }
        if (this.subType === paramTypes.PARAM_LC_HOURS && !this.subTypeId) {
            throw new Error(localize('errors.validation.locationReq', 'Location or Library is required.'));
        }
    }
}

class PatronRecordsAction extends FlowAction {
    constructor({
        id = 0,
        name = '',
        defaultNextAction = -1,
        subType = 0, // @prop {Number}
        dismissText = localize('defaults.tryOther', 'Try something else'),
        messages = [
            localize('defaults.patronRecordPrompt', 'Select which records you would like to view on your patron account. I can renew items for you in your checkouts if allowed by your library. If you would like to complete a different action, select <b>Try Something Else</b>'),
        ], // @prop {Array<String>} messages
        prompt = localize('defaults.patronRecordId', 'Please enter your library card number'),
        prompt2 = localize('defaults.patronRecordPin', 'Please enter your PIN'),
        checkoutsText = localize('defaults.checkoutsText', 'Checkouts'),
        holdsText = localize('defaults.holdsText', 'Holds'),
        finesText = localize('defaults.finesText', 'Fines'),
    }) {
        super({
            id,
            type: flowActionTypes.ACTION_PATRON_RECORDS,
            name,
            defaultNextAction,
            subType,
            messages,
        });
        this.dismissText = dismissText;
        this.prompt = prompt;
        this.prompt2 = prompt2;
        this.checkoutsText = checkoutsText;
        this.holdsText = holdsText;
        this.finesText = finesText;
        /** @prop {Number} stage The part of the action (prompt, contact, question) */
        this.stage = patronRecordStages.CARD_PROMPT;
    }

    /**
     * @param {String text}
     * @returns {Array<String>}
     */
    getMessages(text = '') {
        switch (this.stage) {
            case patronRecordStages.CARD_PROMPT:
                return [this.prompt];
            case patronRecordStages.PIN_PROMPT:
                return [this.prompt2];
            case patronRecordStages.ACTION_PROMPT:
                return this.messages;
            default:
                return [text];
        }
    }

    /**
     * Get responses for this action.
     * @returns {Array<ActionResponse>}
     */
    getResponses() {
        if (this.stage === patronRecordStages.ACTION_PROMPT
            || this.stage === patronRecordStages.RESULTS) {
            const options = [];
            if (this.checkoutsText !== '') {
                options.push(new ActionResponse({
                    id: patronRecordResponses.CHECKOUTS,
                    actionId: this.id,
                    text: this.checkoutsText,
                }));
            }
            if (this.holdsText !== '') {
                options.push(new ActionResponse({
                    id: patronRecordResponses.HOLDS,
                    actionId: this.id,
                    text: this.holdsText,
                }));
            }
            if (this.finesText !== '') {
                options.push(new ActionResponse({
                    id: patronRecordResponses.FINES,
                    actionId: this.id,
                    text: this.finesText,
                }));
            }
            options.push(new ActionResponse({
                id: patronRecordResponses.END,
                actionId: this.id,
                text: this.dismissText,
            }));
            return options;
        }
        return [
            new ActionResponse({
                id: 1,
                actionId: this.id,
                freeResponse: true,
            }),
        ];
    }

    /**
     * Next action result.
     * @param {Number} id Chosen response
     * @returns {ResponseResult}
     */
    getResultForResponse({ id = 0 }) {
        let nextActionId = this.defaultNextAction;
        if (this.stage === patronRecordStages.CARD_PROMPT) {
            this.stage = patronRecordStages.PIN_PROMPT;
            nextActionId = this.id;
        } else if (this.stage === patronRecordStages.PIN_PROMPT) {
            this.stage = patronRecordStages.ACTION_PROMPT;
            nextActionId = this.id;
        } else if (this.stage === patronRecordStages.ACTION_PROMPT) {
            this.stage = patronRecordStages.RESULTS;
            nextActionId = this.id;
        } else if (this.stage === patronRecordStages.RESULTS) {
            if (id <= 0
                || id === patronRecordResponses.END) {
                nextActionId = this.defaultNextAction;
            } else {
                nextActionId = this.id;
            }
        }

        return new ResponseResult({
            nextActionId,
        });
    }

    validate() {
        super.validate();
        if (this.dismissText === '' || (this.checkoutsText === '' && this.holdsText === '' && this.finesText === '')) {
            throw new Error(localize('errors.validation.buttonLabelReq', 'Button labels are required.'));
        }
        if (this.prompt === '' || this.prompt2 === '') {
            throw new Error(localize('errors.validation.promptReq', 'Prompt cannot be blank.'));
        }
        if (this.prompt.length > 500) {
            throw new Error(localize(
                'errors.validation.msgLength', 'Messages and prompts are limited to 500 characters.',
            ));
        }
    }
}

class EndAction extends FlowAction {
    constructor({
        id = flowActionIds.ACTION_END,
        message = '',
    }) {
        super({
            id,
            type: flowActionIds.ACTION_END,
            name: localize('end.default', 'End of Chat'),
            messages: [
                message,
            ],
        });
    }
}

/**
 * Return correct action class for type.
 * @param {Object} obj Action properties.
 * @param {BotFlow} flow Flow
 * @returns {FlowAction}
 */
const actionFactory = function (obj, flow) {
    let action = null;
    if (obj instanceof FlowAction) {
        flow.setResponses(action);
        return action;
    }
    switch (Number(obj.type)) {
        case flowActionTypes.ACTION_QUESTION:
            action = new QuestionAction(obj);
            break;
        case flowActionTypes.ACTION_REQUEST_INFO:
            action = new RequestAction(obj);
            break;
        case flowActionTypes.ACTION_FAQ:
            action = new FAQAction(obj);
            break;
        case flowActionTypes.ACTION_CHAT:
            action = new ChatAction(obj);
            break;
        case flowActionTypes.ACTION_TICKET:
            action = new TicketAction(obj);
            break;
        case flowActionTypes.ACTION_LIBGUIDES:
            action = new LibGuidesAction(obj);
            break;
        case flowActionTypes.ACTION_LIBCAL:
            action = new LibCalAction(obj);
            break;
        case flowActionTypes.ACTION_WELCOME:
            action = new WelcomeAction(obj);
            break;
        case flowActionTypes.ACTION_LINK:
            action = new LinkAction(obj);
            break;
        case flowActionTypes.ACTION_LIBWIZARD:
            action = new LibWizardAction(obj);
            break;
        case flowActionTypes.ACTION_RATING:
            action = new RatingAction(obj);
            break;
        case flowActionTypes.ACTION_PARAMS:
            action = new ParamAction(obj);
            break;
        case flowActionTypes.ACTION_PATRON_RECORDS:
            action = new PatronRecordsAction(obj);
            break;
        default:
            action = new FlowAction(obj);
            break;
    }
    flow.setResponses(action);
    if (obj.previewLink) {
        action.previewLink = obj.previewLink;
    }
    return action;
};

class BotFlow {
    constructor({
        uuid = '',
        iid = 0,
        name = '',
        description = '',
        actions = [],
        timeout = 0, // minutes
        availability = 0,
        message_delay = 2, // seconds
        skip_text = '',
        message_end = '',
        message_chat = '',
        error_generic = '',
        error_input = '',
        provide_email = '',
        bot_name = 'Chatbot',
        profile_url = '',
        profile_color = '',
        search_prompt = '',
        search_retry = '',
        search_continue = '',
        search_resolved = '',
        link_prompt = '',
        link_yes = '',
        link_no = '',
        use_autoload = false,
        questions = [],
        customHours = {},
    }, emitter) {
        this.uuid = uuid;
        this.iid = Number(iid);
        this.name = name;
        this.description = description;
        this.timeout = timeout;
        this.availability = Number(availability);
        this.message_delay = Number(message_delay);
        this.skip_bot = false;
        this.skip_text = skip_text;
        this.message_end = message_end;
        this.message_chat = message_chat;
        this.error_generic = error_generic;
        this.error_input = error_input;
        this.provide_email = provide_email;
        this.bot_name = bot_name;
        this.profile_url = profile_url;
        this.profile_color = profile_color;
        this.search_prompt = search_prompt;
        this.search_retry = search_retry;
        this.search_continue = search_continue;
        this.search_resolved = search_resolved;
        this.link_prompt = link_prompt;
        this.link_yes = link_yes;
        this.link_no = link_no;
        this.use_autoload = use_autoload;
        this.customHours = customHours;
        // Make sure actions are right class.
        this.actions = [];
        this.setActions(actions);
        this.setQuestions(questions);
        if (this.skip_bot) {
            const welcome = this.getWelcomeAction();
            welcome.skip_bot = this.skip_bot;
            if (this.skip_text !== '') {
                welcome.skip_text = this.skip_text;
            }
        }

        this.emitter = emitter;
    }

    getName() {
        return this.bot_name;
    }

    /**
     * Profile data for display.
     * @returns {Object}
     */
    getProfileImage() {
        return {
            path: this.profile_url,
            color: this.profile_color,
        };
    }

    isItDuringCustomHours() {
        const now = new Date();
        const day = now.getUTCDay();
        const hour = now.getUTCHours();
        const minute = now.getUTCMinutes();
        const times = this.customHours;
        if (!times || !times[day] || times[day].length === 0) {
            return false;
        }
        const ranges = times[day];
        return isCoverageInRange(hour, minute, ranges);
    }

    /**
     * Is the flow available when chat is offline.
     * @returns {Boolean}
     */
    isAvailableOffline() {
        if (this.availability === availabilityTypes.AVAIL_ALWAYS
            || this.availability === availabilityTypes.AVAIL_OFFLINE_ONLY) {
            return true;
        }
        if (this.availability !== availabilityTypes.AVAIL_CUSTOM_HOURS) {
            return false;
        }
        // check hours.
        return this.isItDuringCustomHours();
    }

    /**
     * Is the flow available when chat is online.
     * @param {StatusResponse|false} online_rule Widget's current status
     * @returns {Boolean}
     */
    isAvailableOnline(online_rule = false) {
        if (this.availability === availabilityTypes.AVAIL_ALWAYS) {
            return true;
        }
        if (this.availability === availabilityTypes.AVAIL_CUSTOM_HOURS) {
            // check hours
            if (this.isItDuringCustomHours()) {
                return true;
            }
        }
        if (!online_rule || !online_rule.isOnline) {
            return false;
        }
        if (this.availability === availabilityTypes.AVAIL_ONLINE) {
            return true;
        }
        if (this.availability === availabilityTypes.AVAIL_COOP_ONLY
            && online_rule.coopId !== '') {
            return true;
        }
        return false;
    }

    getActions() {
        return this.actions;
    }

    getActionNames() {
        const names = {};
        this.getActions().forEach((action) => {
            names[action.id] = action.name;
        });
        names[-1] = localize('actionform.options.endChat', 'End Chat');
        return names;
    }

    getAction(actionId = 0) {
        return this.actions.find((act) => {
            return act.id === actionId;
        });
    }

    getActionsByType(typeId = 0) {
        return this.actions.filter((act) => {
            return act.type === typeId;
        });
    }

    getWelcomeAction() {
        return this.actions.find((act) => {
            return act.type === flowActionTypes.ACTION_WELCOME;
        });
    }

    /**
     * Add a new action.
     * Will accept a TempAction instance
     * @param {Number} type Type of action
     * @param {String} name Name of action
     * @param {Number} defaultNextAction Default next action.
     * @returns {FlowAction}
     * @throws {Error} if action max count is reached
     */
    addAction({ type, name = '', defaultNextAction = -1 }) {
        if (!this.allowNewActions()) {
            throw new Error(
                localize(
                    'errors.validation.actionLimit',

                    'You have reached the allowed number of actions (%maxLimit%) in a single flow. Please remove actions that are no longer needed to add more.',
                    {
                        maxLimit: MAX_ACTIONS,
                    },
                ),
            );
        }
        let maxId = 0;
        this.actions.forEach((act) => {
            if (act.id > maxId) {
                maxId = act.id;
            }
        });

        const action = actionFactory({
            id: maxId + 1,
            type,
            name,
            defaultNextAction,
        }, this);

        this.actions.push(action);
        return action;
    }

    /**
     * Set a bunch of actions.
     * @param {Array<FlowAction|Object>} actions Action data.
     */
    setActions(actions) {
        actions.forEach((action) => {
            action = actionFactory(action, this);
            this.actions.push(action);
        });
    }

    /**
     * Replace an action with an updated version of it.
     * @param {FlowAction} action Updated action.
     */
    updateAction(action) {
        const actionId = action.id;
        const index = this.actions.findIndex((act) => {
            return act.id === actionId;
        });
        if (index === -1) {
            throw new Error(`Action ${actionId} not found to update.`);
        }
        this.actions.splice(index, 1, action);
    }

    removeAction(actionId) {
        const index = this.actions.findIndex((action) => {
            return action.id === actionId;
        });
        if (index > -1) {
            const [oldAction] = this.actions.splice(index, 1);

            // Switch any targets to the removed action's next action.
            this.actions.forEach((act) => {
                if (act.defaultNextAction === actionId) {
                    act.defaultNextAction = oldAction.defaultNextAction || flowActionIds.ACTION_END;
                }
                act.rules.forEach((rule) => {
                    if (rule.target === actionId) {
                        rule.target = oldAction.defaultNextAction || act.defaultNextAction;
                    }
                });
            });
        }
    }

    /**
     * Add questions to actions.
     * @param {Array<Object>} questions Question data.
     */
    setQuestions(questions) {
        this.actions.forEach((action) => {
            if (action.type !== flowActionTypes.ACTION_QUESTION) {
                return;
            }
            const question = questions.find((q) => {
                return q.id === action.subType;
            });
            if (!question) {
                return;
            }
            action.setQuestion(question);
        });
    }

    /**
     * Set responses on actions that require flow data for it.
     * @param {FlowAction} action current action
     */
    setResponses(action) {
        if (action.isResourceAction()) {
            action.responses = this.getResourceResponses(action.id);
            action.link_prompt = this.link_prompt;
            return;
        }
        if (action.isSearchAction()) {
            action.responses = this.getSearchResultResponses(action.id);
            action.search_prompt = this.search_prompt;
        }
    }

    /**
     * Responses for any search action results.
     * @param {Number} actionId Search action id
     * @returns {Array<ActionResponse>}
     */
    getSearchResultResponses(actionId) {
        return [
            new ActionResponse({
                id: searchResponseTypes.RETRY,
                actionId,
                text: this.search_retry,
                otherResponse: true,
            }),
            new ActionResponse({
                id: searchResponseTypes.UNRESOLVED,
                actionId,
                text: this.search_continue,
            }),
            new ActionResponse({
                id: searchResponseTypes.RESOLVED,
                actionId,
                text: this.search_resolved,
            }),
        ];
    }

    /**
     * Responses for any resource actions.
     * @param {Number} actionId Link action id
     * @returns {Array<ActionResponse>}
     */
    getResourceResponses(actionId = 0) {
        return [
            new ActionResponse({
                id: linkResponseTypes.YES,
                actionId,
                text: this.link_yes,
            }),
            new ActionResponse({
                id: linkResponseTypes.NO,
                actionId,
                text: this.link_no,
            }),
        ];
    }

    /**
     * Find actions that can lead to a specific action.
     * @param {Number} targetId Action id
     * @returns {Array<FlowAction>}
     */
    getActionsThatPointAtTarget(targetId) {
        const origins = [];
        this.getActions().forEach((act) => {
            if (act.id === targetId) {
                return;
            }
            if (act.defaultNextAction === targetId) {
                origins.push(act);
                return;
            }
            let includeAct = false;
            act.rules.forEach((rule) => {
                if (rule.target === targetId) {
                    includeAct = true;
                }
            });
            if (includeAct) {
                origins.push(act);
            }
        });
        return origins;
    }

    /**
     * Have we reached max action yet.
     * @returns {Boolean}
     */
    allowNewActions() {
        return this.getActions().length < MAX_ACTIONS;
    }

    /**
     * When transferring a bot, set some props from the old flow
     * on the new fkow to avoid confusion
     * @param {BotFlow} flow Previous flow
     */
    transferFlow(flow) {
        this.bot_name = flow.bot_name;
        this.profile_url = flow.profile_url;
        this.profile_color = flow.profile_color;
        this.message_delay = flow.message_delay;
    }

    toJSON() {
        return {
            uuid: this.uuid,
            iid: this.iid,
            name: this.name,
            description: this.description,
            actions: this.actions.map((act) => act.toJSON()),
            timeout: this.timeout,
            availability: this.availability,
            message_delay: this.message_delay,
            skip_bot: false,
            skip_text: this.skip_text,
            message_end: this.message_end,
            message_chat: this.message_chat,
            error_generic: this.error_generic,
            error_input: this.error_input,
            provide_email: this.provide_email,
            bot_name: this.bot_name,
            profile_url: this.profile_url,
            profile_color: this.profile_color,
            search_prompt: this.search_prompt,
            search_retry: this.search_retry,
            search_continue: this.search_continue,
            search_resolved: this.search_resolved,
            link_prompt: this.link_prompt,
            link_yes: this.link_yes,
            link_no: this.link_no,
            use_autoload: this.use_autoload,
        };
    }
}

/**
 * Try to deep clone an action.
 * @param {FlowAction} action Action to clone
 * @param {BotFlow} flow Parent flow
 * @returns {FlowAction}
 */
const cloneAction = function (action, flow) {
    const clone = JSON.parse(JSON.stringify(action));
    return actionFactory(clone, flow);
};
/**
 * Clone a rule
 * @param {FlowRule} rule Rule to clone
 * @returns {FlowRule}
 */
const cloneRule = function (rule) {
    return new FlowRule(JSON.parse(JSON.stringify(rule)));
};

export {
    BotFlow,
    FlowAction,
    FlowRule,
    ResponseResult,
    EndAction,
    TempAction,
    ParamAction,
    cloneAction,
    cloneRule,
    LibCalFilterData,
    AppointmentParams,
};
